"use client";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";

// ============================================================================
// Types
// ============================================================================

export interface CarouselRootProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Current active page (controlled) */
  page?: number;
  /** Default page for uncontrolled mode */
  defaultPage?: number;
  /** Called when page changes */
  onPageChange?: (page: number) => void;
  /** Orientation of the carousel */
  orientation?: "horizontal" | "vertical";
  /** Whether to loop back to start/end */
  loop?: boolean;
  /** Enable autoplay */
  autoplay?: boolean | { delay?: number };
  /** Autoplay delay in milliseconds */
  autoplayDelay?: number;
  /** Allow mouse/touch dragging */
  allowMouseDrag?: boolean;
  /** Spacing between slides */
  spacing?: string;
  /** Number of slides to show at once */
  slidesPerView?: number;
  /** Number of slides to scroll at once */
  slidesPerMove?: number;
  children: React.ReactNode;
}

export interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Index of this item */
  index: number;
  children: React.ReactNode;
}

export interface CarouselControlProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface CarouselTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

export interface CarouselIndicatorProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Index of the slide to navigate to */
  index: number;
  children?: React.ReactNode;
}

export interface CarouselItemGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

// ============================================================================
// Context
// ============================================================================

interface CarouselContextValue {
  currentPage: number;
  totalPages: number;
  orientation: "horizontal" | "vertical";
  loop: boolean;
  spacing: string;
  slidesPerView: number;
  goToPage: (page: number) => void;
  goToNext: () => void;
  goToPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  isAutoplayActive: boolean;
  toggleAutoplay: () => void;
  itemsRef: React.RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  allowMouseDrag: boolean;
  handleDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  handleDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  handleDragEnd: () => void;
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null);

function useCarouselContext() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("Carousel components must be used within a CarouselRoot");
  }
  return context;
}

// ============================================================================
// CarouselRoot
// ============================================================================

/**
 * CarouselRoot - Main carousel container with state management and navigation logic.
 * Handles page state, autoplay, looping, and drag interactions.
 */
export function CarouselRoot({
  page: controlledPage,
  defaultPage = 0,
  onPageChange,
  orientation = "horizontal",
  loop = false,
  autoplay = false,
  autoplayDelay = 4000,
  allowMouseDrag = false,
  spacing = "1rem",
  slidesPerView = 1,
  slidesPerMove = 1,
  className,
  children,
  ...props
}: CarouselRootProps) {
  const isControlled = controlledPage !== undefined;
  const [internalPage, setInternalPage] = React.useState(defaultPage);
  const currentPage = isControlled ? controlledPage : internalPage;

  const itemsRef = React.useRef<HTMLDivElement>(null);
  const [totalPages, setTotalPages] = React.useState(0);
  const [isAutoplayActive, setIsAutoplayActive] = React.useState(typeof autoplay === "boolean" ? autoplay : !!autoplay);
  const [isDragging, setIsDragging] = React.useState(false);
  const autoplayTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const dragStartRef = React.useRef<{ x: number; y: number } | null>(null);

  const actualAutoplayDelay =
    typeof autoplay === "object" && autoplay !== null && autoplay.delay ? autoplay.delay : autoplayDelay;

  // Calculate total pages based on children
  React.useEffect(() => {
    if (itemsRef.current) {
      // Validate slidesPerView to prevent division by zero or invalid values
      const validSlidesPerView = Math.max(1, Math.floor(Number(slidesPerView) || 1));
      const items = itemsRef.current.children;

      if (items.length === 0) {
        setTotalPages(0);
        return;
      }

      const pages = Math.ceil(items.length / validSlidesPerView);
      setTotalPages(pages);
    }
  }, [children, slidesPerView]);

  // Scroll to the current page when it changes (for controlled mode)
  // Use a ref to track the last scrolled page to avoid duplicate scrolls
  const lastScrolledPageRef = React.useRef<number>(-1);

  React.useEffect(() => {
    // Only scroll if the page actually changed
    if (currentPage === lastScrolledPageRef.current) {
      return;
    }

    if (itemsRef.current) {
      const items = Array.from(itemsRef.current.children);

      if (items.length === 0) {
        return;
      }

      // Bounds check: clamp targetIndex to valid range
      const targetIndex = currentPage * slidesPerView;
      const clampedIndex = Math.min(targetIndex, items.length - 1);
      const targetItem = items[clampedIndex] as HTMLElement;

      if (targetItem && typeof targetItem.scrollIntoView === "function") {
        targetItem.scrollIntoView({
          behavior: "smooth",
          block: orientation === "vertical" ? "start" : "nearest",
          inline: orientation === "horizontal" ? "start" : "nearest",
        });
        lastScrolledPageRef.current = currentPage;
      }
    }
  }, [currentPage, slidesPerView, orientation]);

  const goToPage = React.useCallback(
    (newPage: number) => {
      let targetPage = newPage;

      if (loop) {
        // Handle looping
        if (targetPage < 0) {
          targetPage = totalPages - 1;
        } else if (targetPage >= totalPages) {
          targetPage = 0;
        }
      } else {
        // Clamp to valid range
        targetPage = Math.max(0, Math.min(newPage, totalPages - 1));
      }

      if (isControlled) {
        onPageChange?.(targetPage);
      } else {
        setInternalPage(targetPage);
        onPageChange?.(targetPage);
      }
    },
    [isControlled, loop, onPageChange, totalPages]
  );

  const goToNext = React.useCallback(() => {
    goToPage(currentPage + slidesPerMove);
  }, [currentPage, goToPage, slidesPerMove]);

  const goToPrev = React.useCallback(() => {
    goToPage(currentPage - slidesPerMove);
  }, [currentPage, goToPage, slidesPerMove]);

  // Keep a stable ref to the latest goToNext for autoplay
  const goToNextRef = React.useRef(goToNext);
  React.useEffect(() => {
    goToNextRef.current = goToNext;
  }, [goToNext]);

  const canGoNext = totalPages > 0 && (loop || currentPage < totalPages - 1);
  const canGoPrev = totalPages > 0 && (loop || currentPage > 0);

  const toggleAutoplay = React.useCallback(() => {
    setIsAutoplayActive((prev) => !prev);
  }, []);

  // Autoplay functionality
  // Use a ref to avoid recreating the interval on every slide change
  React.useEffect(() => {
    if (isAutoplayActive && totalPages > 1) {
      autoplayTimerRef.current = setInterval(() => {
        goToNextRef.current();
      }, actualAutoplayDelay);

      return () => {
        if (autoplayTimerRef.current) {
          clearInterval(autoplayTimerRef.current);
          autoplayTimerRef.current = null;
        }
      };
    }
  }, [isAutoplayActive, totalPages, actualAutoplayDelay]);

  // Pause autoplay on hover
  const handleMouseEnter = React.useCallback(() => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = null;
    }
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (isAutoplayActive && totalPages > 1) {
      autoplayTimerRef.current = setInterval(() => {
        goToNextRef.current();
      }, actualAutoplayDelay);
    }
  }, [isAutoplayActive, totalPages, actualAutoplayDelay]);

  // Mouse/touch drag functionality (to be passed to ItemGroup)
  const handleDragStart = React.useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!allowMouseDrag) return;
      setIsDragging(true);

      // Validate touch events have touches array with elements
      const clientX =
        "touches" in e && e.touches && e.touches.length > 0 ? e.touches[0].clientX : "clientX" in e ? e.clientX : 0;
      const clientY =
        "touches" in e && e.touches && e.touches.length > 0 ? e.touches[0].clientY : "clientY" in e ? e.clientY : 0;
      dragStartRef.current = { x: clientX, y: clientY };
    },
    [allowMouseDrag]
  );

  const handleDragMove = React.useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDragging || !dragStartRef.current) return;

      // Validate touch events have touches array with elements
      const clientX =
        "touches" in e && e.touches && e.touches.length > 0 ? e.touches[0].clientX : "clientX" in e ? e.clientX : 0;
      const clientY =
        "touches" in e && e.touches && e.touches.length > 0 ? e.touches[0].clientY : "clientY" in e ? e.clientY : 0;
      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;
      const threshold = 50;

      if (orientation === "horizontal" && Math.abs(deltaX) > threshold) {
        if (deltaX > 0) {
          goToPrev();
        } else {
          goToNext();
        }
        setIsDragging(false);
        dragStartRef.current = null;
      } else if (orientation === "vertical" && Math.abs(deltaY) > threshold) {
        if (deltaY > 0) {
          goToPrev();
        } else {
          goToNext();
        }
        setIsDragging(false);
        dragStartRef.current = null;
      }
    },
    [isDragging, orientation, goToNext, goToPrev]
  );

  const handleDragEnd = React.useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  const contextValue: CarouselContextValue = {
    currentPage,
    totalPages,
    orientation,
    loop,
    spacing,
    slidesPerView,
    goToPage,
    goToNext,
    goToPrev,
    canGoNext,
    canGoPrev,
    isAutoplayActive,
    toggleAutoplay,
    itemsRef,
    isDragging,
    allowMouseDrag,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
  };

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        {...props}
        className={twMerge("relative w-full", orientation === "vertical" ? "h-full" : "", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

// ============================================================================
// CarouselItemGroup
// ============================================================================

/**
 * CarouselItemGroup - Container for carousel items with scrolling behavior.
 * Manages scroll snap, drag interactions, and layout spacing.
 */
export function CarouselItemGroup({ className, children, ...props }: CarouselItemGroupProps) {
  const { itemsRef, orientation, spacing, isDragging, allowMouseDrag, handleDragStart, handleDragMove, handleDragEnd } =
    useCarouselContext();

  return (
    <div
      ref={itemsRef}
      {...props}
      className={twMerge(
        "flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide scroll-smooth",
        orientation === "vertical" && "flex-col overflow-y-auto overflow-x-hidden snap-y",
        allowMouseDrag && isDragging && "cursor-grabbing select-none",
        allowMouseDrag && !isDragging && "cursor-grab",
        className
      )}
      style={
        {
          gap: spacing,
          "--carousel-spacing": spacing,
          ...props.style,
        } as React.CSSProperties
      }
      onMouseDown={handleDragStart}
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={handleDragStart}
      onTouchMove={handleDragMove}
      onTouchEnd={handleDragEnd}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CarouselItem
// ============================================================================

/**
 * Individual slide/item within the carousel.
 * Handles sizing based on slidesPerView and applies snap alignment.
 */
export function CarouselItem({ index, className, children, ...props }: CarouselItemProps) {
  const { orientation, slidesPerView } = useCarouselContext();

  return (
    <div
      {...props}
      data-index={index}
      className={twMerge("shrink-0 snap-start", orientation === "horizontal" ? "h-full" : "w-full", className)}
      style={{
        width:
          orientation === "horizontal"
            ? `calc((100% - (${slidesPerView - 1} * var(--carousel-spacing, 1rem))) / ${slidesPerView})`
            : undefined,
        height:
          orientation === "vertical"
            ? `calc((100% - (${slidesPerView - 1} * var(--carousel-spacing, 1rem))) / ${slidesPerView})`
            : undefined,
        ...props.style,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CarouselControl
// ============================================================================

/**
 * CarouselControl - Container for navigation controls and indicators.
 * Typically holds prev/next buttons, indicators, and autoplay controls.
 */
export function CarouselControl({ className, children, ...props }: CarouselControlProps) {
  return (
    <div {...props} className={twMerge("flex items-center justify-center gap-4 mt-4", className)}>
      {children}
    </div>
  );
}

// ============================================================================
// CarouselPrevTrigger
// ============================================================================

/**
 * CarouselPrevTrigger - Button to navigate to the previous slide.
 * Automatically disabled when at the first slide (unless loop is enabled).
 */
export function CarouselPrevTrigger({ className, children, ...props }: CarouselTriggerProps) {
  const { goToPrev, canGoPrev } = useCarouselContext();

  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        goToPrev();
        props.onClick?.(e);
      }}
      disabled={!canGoPrev || props.disabled}
      aria-label="Previous slide"
      className={twMerge(
        "inline-flex items-center justify-center rounded-md",
        "h-10 w-10",
        "bg-background-input border border-border-input",
        "text-text-body hover:bg-background-muted",
        "transition-colors duration-200",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-action-default",
        className
      )}
    >
      {children || <Icon name="CaretLeft" size="sm" />}
    </button>
  );
}

// ============================================================================
// CarouselNextTrigger
// ============================================================================

/**
 * CarouselNextTrigger - Button to navigate to the next slide.
 * Automatically disabled when at the last slide (unless loop is enabled).
 */
export function CarouselNextTrigger({ className, children, ...props }: CarouselTriggerProps) {
  const { goToNext, canGoNext } = useCarouselContext();

  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        goToNext();
        props.onClick?.(e);
      }}
      disabled={!canGoNext || props.disabled}
      aria-label="Next slide"
      className={twMerge(
        "inline-flex items-center justify-center rounded-md",
        "h-10 w-10",
        "bg-background-input border border-border-input",
        "text-text-body hover:bg-background-muted",
        "transition-colors duration-200",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-action-default",
        className
      )}
    >
      {children || <Icon name="CaretRight" size="sm" />}
    </button>
  );
}

// ============================================================================
// CarouselIndicator
// ============================================================================

/**
 * CarouselIndicator - Dot indicator for a specific slide.
 * Clicking navigates to the slide at the specified index.
 */
export function CarouselIndicator({ index, className, children, ...props }: CarouselIndicatorProps) {
  const { currentPage, goToPage } = useCarouselContext();
  const isActive = currentPage === index;

  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        goToPage(index);
        props.onClick?.(e);
      }}
      aria-label={`Go to slide ${index + 1}`}
      aria-current={isActive}
      className={twMerge(
        "h-2 rounded-full transition-all duration-200",
        isActive ? "w-6 bg-action-brand" : "w-2 bg-border-muted hover:bg-border-input",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-action-default",
        className
      )}
    >
      {children}
    </button>
  );
}

// ============================================================================
// CarouselIndicatorGroup
// ============================================================================

/**
 * CarouselIndicatorGroup - Container for all page indicators.
 * Automatically generates indicators for each page/slide.
 */
export function CarouselIndicatorGroup({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { totalPages } = useCarouselContext();

  return (
    <div
      {...props}
      className={twMerge("flex items-center gap-2", className)}
      role="group"
      aria-label="Carousel indicators"
    >
      {Array.from({ length: totalPages }, (_, i) => (
        <CarouselIndicator key={i} index={i} />
      ))}
    </div>
  );
}

// ============================================================================
// CarouselAutoplayTrigger
// ============================================================================

/**
 * CarouselAutoplayTrigger - Toggle button for autoplay functionality.
 * Allows users to pause and resume automatic slide transitions.
 */
export function CarouselAutoplayTrigger({ className, children, ...props }: CarouselTriggerProps) {
  const { isAutoplayActive, toggleAutoplay } = useCarouselContext();

  return (
    <button
      type="button"
      {...props}
      onClick={(e) => {
        toggleAutoplay();
        props.onClick?.(e);
      }}
      aria-label={isAutoplayActive ? "Pause autoplay" : "Start autoplay"}
      className={twMerge(
        "inline-flex items-center justify-center rounded-md",
        "h-10 w-10",
        "bg-background-input border border-border-input",
        "text-text-body hover:bg-background-muted",
        "transition-colors duration-200",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-action-default",
        className
      )}
    >
      {children || <Icon name={isAutoplayActive ? "Pause" : "Play"} size="sm" />}
    </button>
  );
}

// ============================================================================
// CarouselProgressText
// ============================================================================

/**
 * CarouselProgressText - Displays current slide position (e.g., "1 / 5").
 * Shows the current page number and total number of pages.
 */
export function CarouselProgressText({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const { currentPage, totalPages } = useCarouselContext();

  return (
    <div
      {...props}
      className={twMerge("text-sm text-text-muted tabular-nums", className)}
      aria-live="polite"
      aria-atomic="true"
    >
      {currentPage + 1} / {totalPages}
    </div>
  );
}
